import { useEffect, useRef } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { useAuth } from '../hooks/useAuth';
import type { AllowedOrgType } from '../guards/permissions';
import { ROUTE_PERMISSIONS } from '../guards/route-permissions';
import { AppRoutes } from '../routes';

/**
 * Detail routes whose content belongs to a single organization, mapped to the
 * list page to land on after a switch. `/parts/import` also matches
 * `/parts/:id` — intentional: a half-done import belongs to the old org too.
 */
const ORG_SCOPED_ROUTE_FALLBACKS: Record<string, string> = {
  '/parts/:id': '/parts',
  '/orders/management/:id': '/orders',
  '/orders/:id': '/orders',
};

export function OrgChangeRedirect() {
  const { organization } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prevOrgIdRef = useRef<string | undefined>(undefined);
  const lastOrgIdRef = useRef<string | undefined>(undefined);
  const locationRef = useRef(location);
  locationRef.current = location;

  if (organization?.id) lastOrgIdRef.current = organization.id;

  useEffect(() => {
    const nextOrgId = organization?.id;

    if (!nextOrgId) return;
    const prevOrgId = prevOrgIdRef.current;
    prevOrgIdRef.current = nextOrgId;
    if (!prevOrgId || prevOrgId === nextOrgId) return;

    const { pathname } = locationRef.current;

    const fallback = Object.entries(ORG_SCOPED_ROUTE_FALLBACKS).find(([pattern]) =>
      matchPath(pattern, pathname),
    )?.[1];
    if (fallback) {
      navigate(fallback, { replace: true });
      return;
    }

    const routePerm = Object.entries(ROUTE_PERMISSIONS).find(([pattern]) =>
      matchPath(pattern, pathname),
    )?.[1];
    const allowedOrgTypes = routePerm?.allowedOrgTypes;
    const orgType = organization?.type?.toLowerCase();
    if (
      allowedOrgTypes &&
      allowedOrgTypes.length > 0 &&
      orgType &&
      !allowedOrgTypes.includes(orgType as AllowedOrgType)
    ) {
      navigate('/', { replace: true });
    }
  }, [organization, navigate]);

  return <AppRoutes key={lastOrgIdRef.current ?? 'initial'} />;
}
